import { Decimal } from 'decimal.js';
import {
  Cart,
  CartDelivery,
  CartDeliveryId,
  CartItem,
  CartItemId,
  CartUtilsStore,
} from '../';
import { getVolumeDiscountPriceByQuantity } from '../volumeDiscounts';
import { createSelector } from '@reduxjs/toolkit';

export const selectCartHasItems =
  (cartKey: string) => (state: CartUtilsStore) => {
    const cart = selectCartByKey(cartKey)(state);
    if (!cart) {
      return null;
    }
    return Object.entries(cart.deliveries).length > 0;
  };

export const selectCartLength =
  (cartKey: string) => (state: CartUtilsStore) => {
    const cart = selectCartByKey(cartKey)(state);
    if (!cart) {
      return 0;
    }
    return cart.itemCount;
  };

export const selectCartByKey = (cartKey: string) => (state: CartUtilsStore) => {
  if (!state.cart[cartKey]) {
    return null;
  }
  return state.cart[cartKey];
};

export type OrderItemsTotalSum = {
  totalNet: number | null;
  totalGross: number | null;
  totalTax: number | null;
};

export type CalculatedCartItem = CartItem & {
  discountedPrice?: number;
  lineDiscountedTotalNet?: number;
  lineDiscountedTax?: number;
};
export type CalculatedCartDelivery = CartDelivery & {
  items: Record<CartItemId, CalculatedCartItem>;
};
export type CalculatedCart = Cart & {
  deliveries: Record<CartDeliveryId, CalculatedCartDelivery>;
  totalDiscounts?: number;
};

const selectCartState = (state: CartUtilsStore) => state.cart;

interface SelectComputedCartOptions {
    ignoreVolumeDiscounts?: boolean;
}

export const selectComputedCartByKey = (
  cartKey: string,
  options?: SelectComputedCartOptions,
) =>
  createSelector([selectCartState], (cartState) => {
    const cart = cartState[cartKey];
    if (!cart) return null;

    // Create a new calculated cart to avoid mutating the original
    const calculatedCart: CalculatedCart = {
      ...cart,
      deliveries: {},
      totalDiscounts: 0,
    };

    // Process each delivery in the cart
    for (const [deliveryId, originalDelivery] of Object.entries(
      cart.deliveries,
    )) {
      const delivery: CalculatedCartDelivery = {
        ...originalDelivery,
        items: {},
      };

      // Process each item in the delivery
      for (const [itemId, originalItem] of Object.entries(
        originalDelivery.items,
      )) {
        const item: CalculatedCartItem = { ...originalItem };
        const hasValidIndividualCustomerPrice =
          item.itemData.hasValidIndividualCustomerPrice;
        const hasLivePrice = item.itemData.hasLivePrice;

        // First check if the item has a valid customer price

        if (
          !hasValidIndividualCustomerPrice &&
          !hasLivePrice &&
          item.itemData.volumeDiscounts?.length &&
          !options?.ignoreVolumeDiscounts
        ) {
          // If no customer price, check for volume discount eligibility
          const volumeDiscountPrice = getVolumeDiscountPriceByQuantity(
            item.itemData,
            item.itemData.volumeDiscounts,
            item.lineTotalQuantity,
          );


          if (volumeDiscountPrice) {
            // Calculate all price-related fields using Decimal.js for precision
            const amount = new Decimal(item.amount);
            const taxRate = new Decimal(item.itemData.taxRate ?? 0);

            // Store the discounted unit price
            item.discountedPrice = volumeDiscountPrice;

            // Calculate the net total (price × quantity)
            item.lineDiscountedTotalNet = new Decimal(volumeDiscountPrice)
              .mul(amount)
              .toNumber();

            // Calculate the tax amount on the discounted total
            item.lineDiscountedTax = new Decimal(item.lineDiscountedTotalNet)
              .mul(taxRate)
              .div(100)
              .toNumber();

            // Calculate how much was saved with this discount
            const originalTotal = new Decimal(item.price || 0).mul(amount);
            const discountedTotal = new Decimal(volumeDiscountPrice).mul(
              amount,
            );
            const itemDiscount = originalTotal
              .minus(discountedTotal)
              .toNumber();

            // Add this item's discount to the cart total
            calculatedCart.totalDiscounts =
              (calculatedCart.totalDiscounts ?? 0) + itemDiscount;
          }
        }

        // Add the processed item back to the delivery
        delivery.items[parseInt(itemId)] = item;
      }

      // Add the processed delivery back to the carti
      calculatedCart.deliveries[deliveryId] = delivery;
    }

    return calculatedCart;
  });

function sumTotalResults(
  p: OrderItemsTotalSum,
  i: OrderItemsTotalSum,
): OrderItemsTotalSum {
  return {
    totalNet: new Decimal(p.totalNet ?? 0).add(i.totalNet ?? 0).toNumber(),
    totalGross: new Decimal(p.totalGross ?? 0).add(i.totalGross ?? 0).toNumber(),
    totalTax: new Decimal(p.totalTax ?? 0).add(i.totalTax ?? 0).toNumber(),
  };
}

const initSumTotalResults = {
  totalNet: 0,
  totalTax: 0,
  totalGross: 0,
};

function sumDeliveryTotals(
  delivery: CalculatedCartDelivery,
): OrderItemsTotalSum {
  return Object.values(delivery.items)
    .map((i) => ({
      totalNet: i.lineDiscountedTotalNet ?? i.lineTotalNet,
      totalTax: i.lineDiscountedTax ?? i.lineTax,
      totalGross: i.lineTotalNet
        ? new Decimal(i.lineDiscountedTotalNet ?? i.lineTotalNet)
            .add(i.lineDiscountedTax ?? i.lineTax ?? 0)
            .toNumber()
        : null,
    }))
    .reduce(sumTotalResults, initSumTotalResults);
}

interface SelectSummaryForCartOptions {
  ignoreVolumeDiscounts?: boolean;
}

export const selectSummaryForCart = (
  cartKey: string,
  options?: SelectSummaryForCartOptions,
) =>
  createSelector([selectComputedCartByKey(cartKey, options)], (cart) => {
    if (!cart) return initSumTotalResults;

    const totals: OrderItemsTotalSum[] = Object.values(cart.deliveries).map(
      sumDeliveryTotals,
    );

    return totals.reduce(
      sumTotalResults,
      initSumTotalResults,
    ) as OrderItemsTotalSum;
  });

export const selectCartTaxRates = (cartKey: string) =>
  createSelector([selectCartByKey(cartKey)], (cart) => {
    if (!cart) return { taxesByPercentage: [], totalPercentes: 0 };

    const deliveries = Object.values(cart.deliveries);

    let totalPercentes = 0;
    const groupedTotals: { [key: string]: number } = {};

    deliveries.forEach((delivery) =>
      Object.values(delivery.items).forEach((item) => {
        const taxRate = item.itemData.taxRate ?? 0;
        const price = (item.price ?? 0) * (item.amount ?? 1);
        const tax = price * (taxRate / 100);
        groupedTotals[taxRate] = (groupedTotals[taxRate] ?? 0) + tax;
        totalPercentes += tax;
      }),
    );

    const taxesByPercentage = Object.entries(groupedTotals).map(
      ([key, value]) => ({
        percent: parseInt(key),
        value: parseFloat(value.toFixed(2)),
      }),
    );

    return { taxesByPercentage, totalPercentes };
  });

export const selectDeliveryById =
  (cartKey: string, deliveryId: string) => (state: CartUtilsStore) => {
    const cart = selectCartByKey(cartKey)(state);
    if (!cart?.deliveries[deliveryId]) {
      console.warn('no delivery found for id ' + deliveryId);
      return null;
    }
    return cart.deliveries[deliveryId];
  };
